// CB:BEGIN admin-priority2-bulk-operations-component
/**
 * Bulk Operations Panel Component
 * Allows admins to perform bulk actions on contractors, projects, and users
 */
import React, { useState } from 'react';
import { useApi } from '../../lib/useApi';

interface BulkOperationsPanelProps {
  selectedIds: string[];
  entityType: 'contractors' | 'projects' | 'users';
  onSuccess: () => void;
  onClearSelection?: () => void;
}

export const BulkOperationsPanel: React.FC<BulkOperationsPanelProps> = ({
  selectedIds,
  entityType,
  onSuccess,
  onClearSelection
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');

  const handleBulkVerify = async () => {
    if (selectedIds.length === 0) {
      alert('No items selected');
      return;
    }

    if (!window.confirm(`Bulk verify ${selectedIds.length} contractors?`)) {
      return;
    }

    const reason = window.prompt('Reason for bulk verification:');
    if (!reason) return;

    setLoading(true);
    try {
      const result = await api.post('/api/admin/bulk/contractors/verify', {
        contractorIds: selectedIds,
        reason
      });
      alert(`✅ Success: ${result.results.success.length}, Failed: ${result.results.failed.length}`);
      if (onClearSelection) onClearSelection();
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Bulk verification failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateStatus = async () => {
    if (!bulkStatus) {
      alert('Please select a status');
      return;
    }

    const reason = window.prompt('Reason for bulk status update:');
    if (!reason) return;

    setLoading(true);
    try {
      const result = await api.post('/api/admin/bulk/projects/update-status', {
        projectIds: selectedIds,
        status: bulkStatus,
        reason
      });
      alert(`✅ Success: ${result.results.success.length}, Failed: ${result.results.failed.length}`);
      setShowBulkStatusModal(false);
      setBulkStatus('');
      if (onClearSelection) onClearSelection();
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Bulk status update failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert('Subject and message are required');
      return;
    }

    setLoading(true);
    try {
      const result = await api.post('/api/admin/bulk/send-email', {
        userIds: selectedIds,
        subject: emailSubject,
        message: emailMessage
      });
      alert(`✅ Email sent: ${result.results.success.length} successful, ${result.results.failed.length} failed`);
      setShowBulkEmailModal(false);
      setEmailSubject('');
      setEmailMessage('');
      if (onClearSelection) onClearSelection();
      onSuccess();
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Bulk email failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const endpoint = `/api/admin/bulk/${entityType}/export`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ filters: {} })
      });

      if (!response.ok) throw new Error('Export failed');

      const csv = await response.text();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      alert('✅ CSV export downloaded');
    } catch (e: any) {
      alert(`❌ Error: ${e.message || 'Export failed'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{
        padding: '16px',
        background: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '16px',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
          Bulk Actions ({selectedIds.length} selected)
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {entityType === 'contractors' && (
            <button
              onClick={handleBulkVerify}
              disabled={loading || selectedIds.length === 0}
              style={{
                padding: '8px 16px',
                background: selectedIds.length === 0 ? '#e5e7eb' : '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {loading ? 'Processing...' : 'Bulk Verify'}
            </button>
          )}

          {entityType === 'projects' && (
            <button
              onClick={() => setShowBulkStatusModal(true)}
              disabled={loading || selectedIds.length === 0}
              style={{
                padding: '8px 16px',
                background: selectedIds.length === 0 ? '#e5e7eb' : '#8b5cf6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Bulk Update Status
            </button>
          )}

          {entityType === 'users' && (
            <button
              onClick={() => setShowBulkEmailModal(true)}
              disabled={loading || selectedIds.length === 0}
              style={{
                padding: '8px 16px',
                background: selectedIds.length === 0 ? '#e5e7eb' : '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Bulk Send Email
            </button>
          )}

          <button
            onClick={handleExportCSV}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {loading ? 'Exporting...' : 'Export to CSV'}
          </button>

          {onClearSelection && selectedIds.length > 0 && (
            <button
              onClick={onClearSelection}
              style={{
                padding: '8px 16px',
                background: '#fff',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowBulkEmailModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Send Bulk Email to {selectedIds.length} Users
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Subject
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Message
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Email message"
                rows={6}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBulkEmailModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEmail}
                disabled={loading || !emailSubject.trim() || !emailMessage.trim()}
                style={{
                  padding: '8px 16px',
                  background: loading ? '#9ca3af' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkStatusModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowBulkStatusModal(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Update Status for {selectedIds.length} Projects
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                New Status
              </label>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select status...</option>
                <option value="PENDING">PENDING</option>
                <option value="IN_PROGRESS">IN_PROGRESS</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBulkStatusModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdateStatus}
                disabled={loading || !bulkStatus}
                style={{
                  padding: '8px 16px',
                  background: loading || !bulkStatus ? '#9ca3af' : '#8b5cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !bulkStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
// CB:END admin-priority2-bulk-operations-component
